import React, { useState } from 'react';
import BackendService from 'BackendService';
import StyledModal from '../styles/StyledModal';
import { SECONDARY_BUTTON_HOVER_SX, StyledButton } from '../styles/StyledButtons';
import { buildCashflowReportCsv, downloadCsv } from './cashflowReportCsvUtils';
import { CashflowReportRows } from './CashflowReportRows';
import type FundOpportunity from '../../models/FundOpportunity';
import type { IRRUpdate } from '../../models/IRRUpdate';

type ToastType = 'error' | 'warning' | 'info' | 'success';
const MODAL_SIZE_SX = {
  '& .MuiDialog-paper': {
    minHeight: '18rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
};

export interface CashflowReportsManageModalProps {
  open: boolean;
  onClose: () => void;
  fundOpportunity: FundOpportunity;
  irrUpdates: IRRUpdate[];
  onDeleted: () => void;
  onShowToast: (message: string, type: ToastType) => void;
}

function useReportActions({
  fundOpportunity,
  onDeleted,
  onShowToast,
}: Pick<CashflowReportsManageModalProps, 'fundOpportunity' | 'onDeleted' | 'onShowToast'>) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IRRUpdate | null>(null);
  const [deleting, setDeleting] = useState(false);

  const download = async (update: IRRUpdate) => {
    try {
      setDownloadingId(update.id);
      const data = await new BackendService().getParsedIRRDataForLabel(
        fundOpportunity.id,
        update.month,
      );
      const csv = buildCashflowReportCsv(update, data);
      const filename = `cashflow-report-${update.label.replace(/\s+/g, '-').toLowerCase()}.csv`;
      downloadCsv(csv, filename);
    } catch (err) {
      console.error('Failed to download cashflow report:', err);
      onShowToast('Unable to download this report. Please try again.', 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await new BackendService().deleteIRRUpdate(deleteTarget.id, fundOpportunity.id);
      onShowToast(`${deleteTarget.label} report deleted.`, 'success');
      setDeleteTarget(null);
      onDeleted();
    } catch (err) {
      console.error('Failed to delete cashflow report:', err);
      onShowToast('Unable to delete this report. Please try again.', 'error');
    } finally {
      setDeleting(false);
    }
  };
  return { downloadingId, deleteTarget, deleting, setDeleteTarget, download, confirmDelete };
}

type ConfirmProps = Pick<
  ReturnType<typeof useReportActions>,
  'deleteTarget' | 'deleting' | 'setDeleteTarget' | 'confirmDelete'
>;
function DeleteReportConfirmation({
  deleteTarget,
  deleting,
  setDeleteTarget,
  confirmDelete,
}: ConfirmProps) {
  return (
    <StyledModal
      open={!!deleteTarget}
      onClose={() => setDeleteTarget(null)}
      title="Delete Cashflow Report"
      fullWidth
      maxWidth="sm"
      sx={MODAL_SIZE_SX}
      subtitle={
        deleteTarget
          ? `Are you sure you want to delete the ${deleteTarget.label} report? This cannot be undone.`
          : undefined
      }
      actions={
        <>
          <StyledButton
            noTopSpacing
            buttonVariant="inactiveOrCancel"
            sx={SECONDARY_BUTTON_HOVER_SX}
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            Cancel
          </StyledButton>
          <StyledButton
            noTopSpacing
            buttonVariant="warning"
            onClick={() => void confirmDelete()}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </StyledButton>
        </>
      }
    >
      <></>
    </StyledModal>
  );
}

/** Lists uploaded reports with download and confirmed deletion actions. */
export default function CashflowReportsManageModal(props: CashflowReportsManageModalProps) {
  const actions = useReportActions(props);
  const sortedUpdates = [...props.irrUpdates].sort((a, b) => b.month.localeCompare(a.month));
  return (
    <>
      <StyledModal
        open={props.open}
        onClose={props.onClose}
        title="Uploaded Cashflow Reports"
        tone="tone1"
        fullWidth
        maxWidth="sm"
        sx={MODAL_SIZE_SX}
      >
        <CashflowReportRows
          reports={sortedUpdates}
          downloadingId={actions.downloadingId}
          onDownload={(report) => void actions.download(report)}
          onDelete={actions.setDeleteTarget}
        />
      </StyledModal>
      <DeleteReportConfirmation {...actions} />
    </>
  );
}
